import { Box, styled, Typography } from '@mui/material';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import useProductsPriceSWR from 'hooks/products/useProductsPriceSWR';
import * as React from 'react';
import { Link } from 'react-router-dom';
import useProducts from '../../../hooks/products/useProducts';
import { CartLineItem } from '../../../models/Cart';
import { RowItems } from '../../../models/TableProps';
import { formatPrice } from '../../../utils/numberFormatter';
import { getCurrency } from '../../../utils/utils';
import { QuantitySelector } from '../../shared/QuantitySelector';

const StyledImage = styled(Box)(() => ({
    width: '90px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    img: {
        maxWidth: '100%',
    },
}));

const StyledTitle = styled(Link)(() => ({
    color: 'black',
    fontWeight: '500',
    '&:hover': {
        textDecoration: 'underline',
    },
}));

const StyledTableCell = styled(TableCell)(() => ({
    padding: '0 6px',
}));

export const CartTableRow: React.FC<RowItems<CartLineItem>> = (props) => {
    const { row, index, handleChange } = props;

    const labelId = `enhanced-table-checkbox-${index}`;

    const products = useProducts();

    const { getMinNetPrice } = useProductsPriceSWR();

    const handleQtyChange = (qty) => {
        handleChange({
            qty: Number(qty),
            rowID: row.id,
            remove: false,
        });
    };

    const handleDelete = () => {
        handleChange({
            qty: 0,
            rowID: row.id,
            remove: true,
        });
    };

    const minNetPrice = React.useMemo((): number => getMinNetPrice(row), [row]);

    const getMinQty = (): number => {
        return products.getMinOrderQtyFromMetafields(
            row.merchandise.product.metafields
        );
    };

    const getMultiplier = (): boolean => {
        return products.getMultiplierFromMetafields(
            row.merchandise.product.metafields
        );
    };

    return (
        <TableRow>
            <StyledTableCell id={labelId} sx={{ width: '50%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ paddingRight: '10px' }}>
                        <StyledImage>
                            <img
                                src={row.merchandise.product.featuredImage.url}
                                alt={row.merchandise.product.title}
                            />
                        </StyledImage>
                    </Box>
                    <Box>
                        <StyledTitle
                            to={'/products/' + row.merchandise.product.handle}
                        >
                            {row.merchandise.product.title}
                        </StyledTitle>
                        <Typography>{row.merchandise.sku}</Typography>
                    </Box>
                </Box>
            </StyledTableCell>
            <StyledTableCell id={labelId}>
                {formatPrice(minNetPrice)}
                {getCurrency(row.cost.amountPerQuantity.currencyCode)}
            </StyledTableCell>
            <StyledTableCell
                id={labelId}
                sx={{ width: '120px', padding: '16px 0 16px 0' }}
            >
                <QuantitySelector
                    defaultQty={Number(row.quantity)}
                    qtyChanged={handleQtyChange}
                    minOrderQuantity={getMinQty()}
                    minOrderMultiplier={getMultiplier()}
                />
            </StyledTableCell>
            <StyledTableCell id={labelId}>
                {formatPrice(minNetPrice * row.quantity)}
                {getCurrency(row.cost.totalAmount.currencyCode)}
            </StyledTableCell>
            <StyledTableCell id={labelId}>
                <Typography onClick={handleDelete} sx={{ cursor: 'pointer' }}>
                    X
                </Typography>
            </StyledTableCell>
        </TableRow>
    );
};
